/* eslint-disable @typescript-eslint/no-explicit-any */
import { useFetch, useFetchUncachedCb } from '@gluedigital/ruse-fetch-extras';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStreaming } from 'src/context/StreamingContext';
import { useSuccessToast } from 'src/hooks/useSuccessToast';
import authTokenActions from 'src/store/actions/chatToken';
import messagesActions from 'src/store/actions/message';
import { handleRemoveSources } from 'src/store/actions/sources';
import { WidgetData } from 'src/store/actions/widget';
import { Errors, FeedbackEnum, FullSource, QuestionResponse, Source, SourceURLFeedback } from 'src/types';
import { createBotMessage, createUserMessage } from 'src/utils/createMessage';
import { mapClientSettings } from 'src/utils/mapClientSettings';
import { useAppDispatch } from 'universal-scripts';
import { useIsWidget } from './useIsWidget';
import { useStreamMessage } from './useStreamMessage';

export const useFullHistory = () => {
	const baseApi = window?._env_?.API_URL;
	const chatTokenStore = useSelector((s: { chatToken: string }) => s.chatToken);
	const retrieveHistory = async () => {
		const response = await fetch(`${baseApi}/chat/history`, {
			credentials: 'include',
			headers: {
				'X-Chat-Token': chatTokenStore
			}
		});

		if (!response.ok) return;

		const history = await response.json();

		return history;
	};
	return retrieveHistory;
};

export const useFeedback = ({ questionId }: { questionId: string }) => {
	const baseApi = window?._env_?.API_URL;
	const doFetch = useFetchUncachedCb();
	const dispatch = useDispatch();
	const { openSuccessToast } = useSuccessToast();

	const storeUserFront = useSelector((s: { userFront: string }) => s.userFront);
	const addNameToComment = __DEV__ ? `[${storeUserFront}] ` : '';
	return {
		post: (feedback: boolean, comment?: string, sourceFeedback?: SourceURLFeedback[], internetSourceFeedback?: SourceURLFeedback[]) =>
			doFetch(`${baseApi}/chat/feedback`, {
				body: {
					question_id: questionId,
					feedback,
					source_feedback: sourceFeedback?.map((sourceUrlFeedback) => ({
						id: sourceUrlFeedback.id,
						url: sourceUrlFeedback.url,
						feedback: sourceUrlFeedback.feedback === FeedbackEnum.LIKE,
						internet: false
					})),
					internet_source_feedback: internetSourceFeedback?.map((sourceUrlFeedback) => ({
						id: sourceUrlFeedback.id,
						url: sourceUrlFeedback.url,
						feedback: sourceUrlFeedback.feedback === FeedbackEnum.LIKE,
						internet: true
					})),
					comment: !addNameToComment ? comment : addNameToComment + (comment || '')
				}
			})
				.then(() => {
					openSuccessToast('feedback-modal.received');
				})
				.catch(() => {
					dispatch({ type: 'error/show', payload: Errors.link_feedback_error });
				}),
		remove: () =>
			doFetch(`${baseApi}/chat/feedback?id=${questionId}`, {
				method: 'DELETE'
			}).catch(() => {
				dispatch({ type: 'error/show', payload: Errors.unlink_feedback_error });
			})
	};
};

export const useSources = (sources: Source['url'][], messageId: string): FullSource[] => {
	const [data, setData] = useState<FullSource[]>([]);

	const fetchSources = useFetchUncachedCb();
	const sourcesKey = sources?.join(',') ?? '';

	useEffect(() => {
		if (!sourcesKey) {
			setData([]);
			return;
		}

		const loadSources = async () => {
			try {
				const result = await fetchSources('/api/og', {
					method: 'POST',
					body: { urls: sourcesKey.split(',') }
				});
				setData(Array.isArray(result) ? (result as FullSource[]) : []);
			} catch {
				setData([]);
			}
		};

		loadSources();
	}, [sourcesKey, messageId, fetchSources]);

	return data;
};

export const useQuestionById = (questionId: string): QuestionResponse => {
	const baseApi = window?._env_?.API_URL;
	return useFetch<QuestionResponse>(`${baseApi}/chat/question`, {
		params: {
			id: questionId
		}
	});
};

export const useChatById = (chatId: string) => {
	const dispatch = useDispatch();
	const baseApi = window?._env_?.API_URL;
	const url = `${baseApi}/share/${chatId}`;

	const [data, setData] = useState([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await fetch(url, {
					credentials: 'include'
				});
				const chatToken = response.headers.get('x-chat-token');
				dispatch(authTokenActions.addChatToken(chatToken as string));
				if (!response.ok) {
					throw new Error(`Failed to fetch data from ${url}`);
				}
				const result = await response.json();
				setData(result.sort((a: any, b: any) => a?.date - b?.date));
			} catch (error) {
				console.error(error);
				dispatch({ type: 'error/show', payload: Errors.get_shared_chat_error });
			}
		};

		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [url]);

	return data;
};

export default useFetch;

export const useWidgetById = (id: string | undefined): WidgetData | null => {
	const baseApi = window?._env_?.API_URL;
	const url = `${baseApi}/client/settings/?id=${id}`;
	const raw = useFetch<unknown>(url);
	return mapClientSettings(raw);
};

export const useShareId = (id: string) => {
	const baseApi = window?._env_?.API_URL;
	const chatTokenStore = useSelector((s: { chatToken: string }) => s.chatToken);
	const url = `${baseApi}/share/`;
	const headers: Record<string, string> = {
		'X-Chat-Token': chatTokenStore,
		...(id && { 'X-Question-Id': id })
	};
	return useFetch<string>(
		url,
		{
			credentials: 'include',
			headers
		},
		`share-${!id ? 'chat' : 'question'}`
	);
};

export const useSubmitMessage = ({
	userInput,
	file,
	onChunk,
	onComplete
}: {
	userInput: string;
	file?: File | null;
	onChunk: (chunk: string) => void;
	onComplete: (fullText: string) => void;
}) => {
	const dispatch = useAppDispatch();
	const widget = useIsWidget();
	const { updateStreamInfo, clearStreamInfo } = useStreaming();

	const retrieveMessage = useStreamMessage(userInput, file, widget?.id, {
		onChunk,
		onComplete
	});

	const submit = async () => {
		onComplete('');
		dispatch(handleRemoveSources());

		const userMessage = await createUserMessage(userInput, file || undefined);
		dispatch(messagesActions.addMessage(userMessage));

		clearStreamInfo();
		updateStreamInfo({ isWriting: true });

		try {
			dispatch(messagesActions.addMessage(createBotMessage()));
			await retrieveMessage();
		} catch (err) {
			console.error(err);
		} finally {
			updateStreamInfo({ isWriting: false });
		}
	};

	return submit;
};
